import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, GitCompare, FileText, Newspaper, Check, Plus, X, Link, FileStack, PenTool, ExternalLink, Loader2, Search } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ARTICLE_TYPES, type ArticleTypeId } from '../../types';

// Tipo para items de comparación enriquecidos
export interface ComparisonItem {
  url: string;
  title: string;
  source: 'wp-page' | 'wp-post' | 'external';
  wpId?: number;
  excerpt?: string;
  featuredImage?: string;
}

// Mapeo de iconos
const IconMap: Record<string, React.ElementType> = {
  BookOpen,
  GitCompare,
  FileText,
  Newspaper,
};

type ComparisonSourceTab = 'wp-pages' | 'wp-posts' | 'manual';

interface ArticleTypeSelectorProps {
  selectedType: ArticleTypeId;
  onSelectType: (type: ArticleTypeId) => void;
  onContinue: () => void;
  // Para tipo comparativa
  comparisonUrls?: string[];
  onComparisonUrlsChange?: (urls: string[]) => void;
  comparisonItems?: ComparisonItem[];
  onComparisonItemsChange?: (items: ComparisonItem[]) => void;
  // Para tipo noticia
  mainSourceUrl?: string;
  onMainSourceUrlChange?: (url: string) => void;
  // WordPress connection
  wpCredentials?: { url: string; user: string; appPassword: string } | null;
}

export const ArticleTypeSelector: React.FC<ArticleTypeSelectorProps> = ({
  selectedType,
  onSelectType,
  onContinue,
  comparisonUrls = [],
  onComparisonUrlsChange,
  comparisonItems = [],
  onComparisonItemsChange,
  mainSourceUrl = '',
  onMainSourceUrlChange,
  wpCredentials,
}) => {
  const [newUrl, setNewUrl] = useState('');
  const [sourceTab, setSourceTab] = useState<ComparisonSourceTab>('wp-pages');
  const [wpPages, setWpPages] = useState<any[]>([]);
  const [wpPosts, setWpPosts] = useState<any[]>([]);
  const [wpLoading, setWpLoading] = useState(false);
  const [wpSearch, setWpSearch] = useState('');

  // Cargar páginas/posts de WordPress
  const loadWpContent = useCallback(async (type: 'pages' | 'posts') => {
    if (!wpCredentials?.url) return;

    setWpLoading(true);
    try {
      const authHeader = 'Basic ' + btoa(`${wpCredentials.user}:${wpCredentials.appPassword}`);
      const endpoint = type === 'pages' ? 'pages' : 'posts';
      const response = await fetch(
        `${wpCredentials.url}/wp-json/wp/v2/${endpoint}?per_page=50&status=publish`,
        { headers: { 'Authorization': authHeader } }
      );

      if (response.ok) {
        const data = await response.json();
        if (type === 'pages') {
          setWpPages(data);
        } else {
          setWpPosts(data);
        }
      }
    } catch (error) {
      // Error loading WP content - silently ignore
    } finally {
      setWpLoading(false);
    }
  }, [wpCredentials]);

  // Cargar contenido cuando cambia la pestaña
  useEffect(() => {
    if (selectedType === 'comparative' && wpCredentials?.url) {
      if (sourceTab === 'wp-pages' && wpPages.length === 0) {
        loadWpContent('pages');
      } else if (sourceTab === 'wp-posts' && wpPosts.length === 0) {
        loadWpContent('posts');
      }
    }
  }, [selectedType, sourceTab, wpCredentials, loadWpContent, wpPages.length, wpPosts.length]);

  // Añadir item de WordPress
  const handleAddWpItem = (item: any, source: 'wp-page' | 'wp-post') => {
    if (!onComparisonItemsChange) return;

    const newItem: ComparisonItem = {
      url: item.link,
      title: item.title?.rendered || item.title || 'Sin título',
      source,
      wpId: item.id,
      excerpt: item.excerpt?.rendered?.replace(/<[^>]*>/g, '').slice(0, 150) || '',
      featuredImage: item._embedded?.['wp:featuredmedia']?.[0]?.source_url
    };

    // No duplicar
    if (!comparisonItems.find(i => i.url === newItem.url)) {
      onComparisonItemsChange([...comparisonItems, newItem]);
      // También actualizar comparisonUrls para compatibilidad
      if (onComparisonUrlsChange && !comparisonUrls.includes(newItem.url)) {
        onComparisonUrlsChange([...comparisonUrls, newItem.url]);
      }
    }
  };

  // Añadir URL manual
  const handleAddManualUrl = () => {
    if (!newUrl.trim() || !onComparisonItemsChange) return;

    const url = newUrl.trim();
    const newItem: ComparisonItem = {
      url,
      title: url, // Se actualizará después de extraer
      source: 'external'
    };

    if (!comparisonItems.find(i => i.url === url)) {
      onComparisonItemsChange([...comparisonItems, newItem]);
      if (onComparisonUrlsChange && !comparisonUrls.includes(url)) {
        onComparisonUrlsChange([...comparisonUrls, url]);
      }
    }
    setNewUrl('');
  };

  // Eliminar item
  const handleRemoveItem = (urlToRemove: string) => {
    if (onComparisonItemsChange) {
      onComparisonItemsChange(comparisonItems.filter(i => i.url !== urlToRemove));
    }
    if (onComparisonUrlsChange) {
      onComparisonUrlsChange(comparisonUrls.filter(u => u !== urlToRemove));
    }
  };

  // Filtrar contenido de WordPress por búsqueda
  const filteredWpPages = wpPages.filter(p =>
    (p.title?.rendered || '').toLowerCase().includes(wpSearch.toLowerCase())
  );
  const filteredWpPosts = wpPosts.filter(p =>
    (p.title?.rendered || '').toLowerCase().includes(wpSearch.toLowerCase())
  );

  const selectedConfig = ARTICLE_TYPES.find(t => t.id === selectedType);

  const canContinue = () => {
    if (selectedType === 'comparative') {
      return comparisonItems.length >= 2;
    }
    if (selectedType === 'news') {
      return mainSourceUrl.trim().length > 0;
    }
    return true;
  };

  return (
    <div className="space-y-6">
      <Card className="border border-gray-200 shadow-sm">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            Selecciona el tipo de artículo
          </h2>
          <p className="text-sm text-gray-500">
            Cada tipo tiene un formato y estilo diferente. Elige el que mejor se adapte a tu contenido.
          </p>
        </div>

        {/* Grid de tipos de artículo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {ARTICLE_TYPES.map((type) => {
            const Icon = IconMap[type.icon] || FileText;
            const isSelected = selectedType === type.id;

            return (
              <button
                key={type.id}
                onClick={() => onSelectType(type.id)}
                className={`
                  relative p-4 rounded-xl border-2 text-left transition-all duration-200
                  ${isSelected
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
              >
                {/* Indicador de selección */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                    ${isSelected ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}
                  `}>
                    <Icon className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className={`font-medium text-base mb-0.5 ${isSelected ? 'text-gray-900' : 'text-gray-800'}`}>
                      {type.name}
                    </h3>
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                      {type.description}
                    </p>

                    {/* Features tags */}
                    <div className="flex flex-wrap gap-1">
                      {type.features.slice(0, 3).map((feature, idx) => (
                        <span
                          key={idx}
                          className={`
                            text-[10px] px-2 py-0.5 rounded-full
                            ${isSelected
                              ? 'bg-gray-200 text-gray-700'
                              : 'bg-gray-100 text-gray-500'
                            }
                          `}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Campos adicionales según el tipo seleccionado */}
        {selectedType === 'comparative' && (
          <div className="border-t border-gray-100 pt-6 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Link className="w-4 h-4 text-gray-600" />
              <h3 className="font-medium text-gray-900">Productos/Servicios a comparar</h3>
              <span className="text-xs text-gray-400">(mínimo 2, máximo 5)</span>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Selecciona los productos o servicios que quieres comparar. El artículo extraerá la información y generará secciones detalladas con enlaces.
            </p>

            {/* Lista de items seleccionados */}
            {comparisonItems.length > 0 && (
              <div className="space-y-2 mb-4">
                {comparisonItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <span className="w-6 h-6 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                          {item.source === 'wp-page' ? 'Página' : item.source === 'wp-post' ? 'Post' : 'Externa'}
                        </span>
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {item.title.replace(/<[^>]*>/g, '')}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 truncate block">{item.url}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item.url)}
                      className="p-1.5 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Tabs de fuente */}
            {comparisonItems.length < 5 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Tab headers */}
                <div className="flex border-b border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setSourceTab('wp-pages')}
                    className={`flex-1 px-4 py-2.5 text-xs font-medium flex items-center justify-center gap-2 transition-colors ${
                      sourceTab === 'wp-pages'
                        ? 'bg-white text-gray-900 border-b-2 border-gray-900 -mb-px'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <FileStack className="w-3.5 h-3.5" />
                    WP Páginas
                  </button>
                  <button
                    onClick={() => setSourceTab('wp-posts')}
                    className={`flex-1 px-4 py-2.5 text-xs font-medium flex items-center justify-center gap-2 transition-colors ${
                      sourceTab === 'wp-posts'
                        ? 'bg-white text-gray-900 border-b-2 border-gray-900 -mb-px'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <PenTool className="w-3.5 h-3.5" />
                    WP Blog
                  </button>
                  <button
                    onClick={() => setSourceTab('manual')}
                    className={`flex-1 px-4 py-2.5 text-xs font-medium flex items-center justify-center gap-2 transition-colors ${
                      sourceTab === 'manual'
                        ? 'bg-white text-gray-900 border-b-2 border-gray-900 -mb-px'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    URL Manual
                  </button>
                </div>

                {/* Tab content */}
                <div className="p-4 bg-white">
                  {/* WordPress Pages/Posts */}
                  {(sourceTab === 'wp-pages' || sourceTab === 'wp-posts') && (
                    <>
                      {!wpCredentials?.url ? (
                        <div className="text-center py-6 text-gray-400">
                          <FileStack className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Configura las credenciales de WordPress en el proyecto para ver contenido</p>
                        </div>
                      ) : (
                        <>
                          {/* Búsqueda */}
                          <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder={`Buscar ${sourceTab === 'wp-pages' ? 'páginas' : 'posts'}...`}
                              value={wpSearch}
                              onChange={(e) => setWpSearch(e.target.value)}
                              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                            />
                          </div>

                          {/* Lista de contenido */}
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {wpLoading ? (
                              <div className="flex items-center justify-center py-6 text-gray-400">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                Cargando...
                              </div>
                            ) : (
                              (sourceTab === 'wp-pages' ? filteredWpPages : filteredWpPosts).map((item) => {
                                const isSelected = comparisonItems.some(i => i.url === item.link);
                                return (
                                  <button
                                    key={item.id}
                                    onClick={() => !isSelected && handleAddWpItem(item, sourceTab === 'wp-pages' ? 'wp-page' : 'wp-post')}
                                    disabled={isSelected}
                                    className={`w-full text-left p-2.5 rounded-lg flex items-center gap-2 transition-colors ${
                                      isSelected
                                        ? 'bg-gray-100 border border-gray-200 cursor-default'
                                        : 'hover:bg-gray-50 border border-transparent'
                                    }`}
                                  >
                                    {isSelected ? (
                                      <Check className="w-4 h-4 text-gray-900 flex-shrink-0" />
                                    ) : (
                                      <Plus className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    )}
                                    <span className={`text-sm truncate ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                                      {(item.title?.rendered || item.title || 'Sin título').replace(/<[^>]*>/g, '')}
                                    </span>
                                  </button>
                                );
                              })
                            )}
                            {!wpLoading && (sourceTab === 'wp-pages' ? filteredWpPages : filteredWpPosts).length === 0 && (
                              <p className="text-center py-4 text-sm text-gray-400">
                                No se encontraron {sourceTab === 'wp-pages' ? 'páginas' : 'posts'}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* URL Manual */}
                  {sourceTab === 'manual' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-500">
                        Pega URLs de productos o servicios externos que no estén en WordPress.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://ejemplo.com/producto"
                          value={newUrl}
                          onChange={(e) => setNewUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddManualUrl();
                            }
                          }}
                          className="flex-1"
                        />
                        <Button
                          onClick={handleAddManualUrl}
                          disabled={!newUrl.trim()}
                          variant="outline"
                          icon={<Plus className="w-4 h-4" />}
                        >
                          Añadir
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {comparisonItems.length < 2 && (
              <p className="text-sm text-amber-600 mt-3">
                Necesitas al menos 2 productos/servicios para hacer una comparativa
              </p>
            )}

            {comparisonItems.length >= 2 && (
              <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-700">
                  ✓ Se extraerá información de cada URL y se generará un artículo comparativo detallado
                </p>
              </div>
            )}
          </div>
        )}

        {selectedType === 'news' && (
          <div className="border-t border-gray-100 pt-6 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Newspaper className="w-4 h-4 text-gray-600" />
              <h3 className="font-medium text-gray-900">Fuente principal de la noticia</h3>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Introduce la URL de la fuente principal que contiene la noticia o novedad que quieres comentar.
            </p>

            <Input
              placeholder="https://ejemplo.com/noticia-original"
              value={mainSourceUrl}
              onChange={(e) => onMainSourceUrlChange?.(e.target.value)}
              helperText="Esta URL será analizada para extraer los datos principales de la noticia"
            />
          </div>
        )}

        {/* Resumen del tipo seleccionado */}
        {selectedConfig && (
          <div className="bg-gray-50 rounded-xl p-4 mt-6 border border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-gray-900 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                {React.createElement(IconMap[selectedConfig.icon] || FileText, { className: 'w-4 h-4' })}
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-0.5 text-sm">
                  Tipo seleccionado: {selectedConfig.name}
                </h4>
                <p className="text-xs text-gray-500 mb-2">
                  {selectedConfig.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-[10px] px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                    {selectedConfig.requiresSources ? 'Con fuentes' : 'Sin fuentes'}
                  </span>
                  <span className="text-[10px] px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                    Investigación: {
                      selectedConfig.researchLevel === 'full' ? 'Completa' :
                      selectedConfig.researchLevel === 'urls-only' ? 'Solo URLs' :
                      selectedConfig.researchLevel === 'minimal' ? 'Mínima' : 'Ninguna'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botón continuar */}
        <div className="flex justify-end mt-6 pt-5 border-t border-gray-100">
          <Button
            onClick={onContinue}
            disabled={!canContinue()}
            size="lg"
            className="px-8 bg-gray-900 hover:bg-gray-800 text-white"
          >
            Continuar con {selectedConfig?.name || 'el artículo'}
          </Button>
        </div>
      </Card>
    </div>
  );
};
